interface TokenEntry {
  token?: string
  inFlight?: Promise<string | undefined>
}

// Kept in memory rather than in a cookie: px_auth / px_session are already readable by JS and shared
// across *.healthpilot.com, so adding another readable cookie widens that surface for no benefit.
// One entry per gateway base URL — each gateway issues its own token pair.
const tokens = new Map<string, TokenEntry>()

export class Antiforgery {
  static use() {
    const fetchToken = async (baseUrl: string) => {
      try {
        const result = await $fetch<{ token: string }>(`${baseUrl}/Antiforgery/token`, {
          method: 'GET',
          credentials: 'include',
          headers: { Accept: 'application/json' }
        })

        return result?.token
      } catch (e) {
        // A gateway that doesn't serve the endpoint yet, or a transient failure. Let the request go
        // out without the header rather than breaking the app — the gateway is the enforcer.
        console.warn('Unable to obtain CSRF token', e)
        return undefined
      }
    }

    /**
     * Returns the request token for a gateway, fetching it once and sharing that fetch between
     * concurrent callers.
     */
    const ensure = async (baseUrl: string) => {
      let entry = tokens.get(baseUrl)

      if (_isNil(entry)) {
        entry = {}
        tokens.set(baseUrl, entry)
      }

      if (!_isNil(entry.token)) return entry.token

      if (_isNil(entry.inFlight)) {
        entry.inFlight = fetchToken(baseUrl).then((token) => {
          entry!.token = token
          entry!.inFlight = undefined
          return token
        })
      }

      return entry.inFlight
    }

    /**
     * Drops the cached token so the next request fetches a fresh one. Used after the gateway rejects a
     * token — most likely because its paired cookie is gone (cleared by the user, or a new browser
     * session) while this in-memory token lived on.
     */
    const clear = (baseUrl: string) => {
      tokens.delete(baseUrl)
    }

    return {
      ensure,
      clear
    }
  }
}
